import type { DbTransactionCtx } from '../../../../lib/db.js';
import { ENTITY_SCREENING_REVALIDATION_REASON } from '../../../constants.js';
import { revalidateDismissedMatchesByMatchIds } from '../match-result-validations/revalidate-dismissed-matches.js';
import type { InsertEmbeddingDbParams, TxDbClient } from './types.js';

export interface SyncEntityEmbeddingsPlan {
  entityId: string;
  /** Stored rows whose text is still wanted: untouched, same id. */
  keepIds: string[];
  /** Texts the entity did not have yet, already embedded. */
  newRows: InsertEmbeddingDbParams[];
  /** Stored rows whose text is gone (or duplicated): retired. */
  dropIds: string[];
  /** The Kharon change audit row explaining the retirement; null for UFLPA. */
  changeId: string | null;
}

export interface SyncEmbeddingsDbParams {
  entityType: 'KHARON' | 'UFLPA';
  perEntity: SyncEntityEmbeddingsPlan[];
}

export interface SyncEmbeddingsDbResult {
  embeddingsInserted: number;
  embeddingsDeleted: number;
  /** Reviewed hits on retired aliases carried over to a surviving alias. */
  matchesCarriedOver: number;
  unreviewedMatchesDeleted: number;
  /** Entities whose every row would have gone: left as they were instead. */
  entitiesLeftIntact: number;
}

/** One transaction per this many entities; inserts chunked within it. */
const ENTITIES_PER_TRANSACTION = 50;
const INSERT_BATCH_SIZE = 200;

const REASON_BY_TYPE = {
  KHARON: ENTITY_SCREENING_REVALIDATION_REASON.KHARON_ENTITY_CHANGE,
  UFLPA: ENTITY_SCREENING_REVALIDATION_REASON.UFLPA_ENTITY_CHANGE,
} as const;

/**
 * Carries the reviews on retired alias hits over to the pair's surviving
 * alias, one statement, and reports the rows that now hold them.
 *
 * A match row is one supplier against one alias, unique per
 * (company, entity_type, entity, alias). For every reviewed hit on a retired
 * alias: if the same supplier already has a hit on the survivor, the review
 * moves onto that row (unless that tenant already reviewed it — then their
 * newer decision stands and the retired one goes with its row); if not, the
 * retired row itself becomes the survivor's hit. Two retired aliases of one
 * pair fold into one.
 */
const CARRY_OVER_SQL = `
  WITH retired AS (
    SELECT mr.id, mr.company_id, mr.entity_type, mr.entity_id
    FROM entity_screening_match_results mr
    WHERE mr.entity_embedding_id = ANY(:dropIds)
      AND EXISTS (
        SELECT 1
        FROM entity_screening_matching_results_validations v
        WHERE v.match_result_id = mr.id
      )
  ),
  existing AS (
    SELECT s.id, s.company_id, s.entity_type, s.entity_id
    FROM entity_screening_match_results s
    WHERE s.entity_embedding_id = :survivor
      AND EXISTS (
        SELECT 1 FROM retired r
        WHERE r.company_id = s.company_id
          AND r.entity_type = s.entity_type
          AND r.entity_id = s.entity_id
      )
  ),
  promoted AS (
    SELECT DISTINCT ON (r.company_id, r.entity_type, r.entity_id)
      r.id, r.company_id, r.entity_type, r.entity_id
    FROM retired r
    WHERE NOT EXISTS (
      SELECT 1 FROM existing e
      WHERE e.company_id = r.company_id
        AND e.entity_type = r.entity_type
        AND e.entity_id = r.entity_id
    )
    ORDER BY r.company_id, r.entity_type, r.entity_id, r.id
  ),
  repointed AS (
    UPDATE entity_screening_match_results mr
       SET entity_embedding_id = :survivor
     WHERE mr.id IN (SELECT id FROM promoted)
    RETURNING mr.id
  ),
  targets AS (
    SELECT id, company_id, entity_type, entity_id FROM existing
    UNION ALL
    SELECT id, company_id, entity_type, entity_id FROM promoted
  ),
  movable AS (
    SELECT DISTINCT ON (t.id, rv.validated_by_company_id)
      rv.id AS validation_id,
      t.id AS target_id
    FROM retired r
    JOIN targets t
      ON t.company_id = r.company_id
     AND t.entity_type = r.entity_type
     AND t.entity_id = r.entity_id
     AND t.id <> r.id
    JOIN entity_screening_matching_results_validations rv
      ON rv.match_result_id = r.id
    WHERE NOT EXISTS (
      SELECT 1
      FROM entity_screening_matching_results_validations tv
      WHERE tv.match_result_id = t.id
        AND tv.validated_by_company_id = rv.validated_by_company_id
    )
    ORDER BY t.id, rv.validated_by_company_id, rv.updated_at DESC NULLS LAST
  ),
  moved AS (
    UPDATE entity_screening_matching_results_validations v
       SET match_result_id = m.target_id
      FROM movable m
     WHERE v.id = m.validation_id
    RETURNING m.target_id AS id
  )
  SELECT id FROM repointed
  UNION
  SELECT id FROM moved`;

/**
 * Brings an entity's stored embeddings in line with the texts it should have,
 * without ever deleting a tenant's review.
 *
 * `entity_screening_match_results.entity_embedding_id` cascades from this
 * table and reviews cascade from match results, so retiring a row the naive
 * way erases match history and every FLAGGED / DISMISSED decision on it.
 * Instead, per entity and inside one transaction: insert the new texts; carry
 * every *reviewed* hit on a retired alias over to a surviving alias of the
 * same entity (see `CARRY_OVER_SQL`) and re-open its DISMISSED review with a
 * reason; delete the remaining hits on retired aliases (unreviewed ones, and
 * reviews the tenant had already superseded on the survivor); then delete the
 * rows. An entity that would end up with no row at all is left untouched — an
 * entity without a vector is unmatchable, and the cascade would take its
 * reviews.
 *
 * Callers embed **before** calling this; nothing here talks to OpenAI, so the
 * transaction stays short and never holds row locks across a network call.
 */
export const syncEmbeddingsForEntitiesDb = async (
  db: TxDbClient,
  { entityType, perEntity }: SyncEmbeddingsDbParams,
): Promise<SyncEmbeddingsDbResult> => {
  const result: SyncEmbeddingsDbResult = {
    embeddingsInserted: 0,
    embeddingsDeleted: 0,
    matchesCarriedOver: 0,
    unreviewedMatchesDeleted: 0,
    entitiesLeftIntact: 0,
  };

  const changed = perEntity.filter(
    plan => plan.newRows.length > 0 || plan.dropIds.length > 0,
  );

  for (let i = 0; i < changed.length; i += ENTITIES_PER_TRANSACTION) {
    const chunk = changed.slice(i, i + ENTITIES_PER_TRANSACTION);

    // Built outside the transaction: `normalizeVector` only formats a bind.
    const prepared = chunk.map(plan => ({
      plan,
      rows: plan.newRows.map((row, index) => {
        const { placeholder, binding } = db.normalizeVector(
          row.embedding,
          `embedding_${index}`,
        );
        return { row, index, placeholder, binding };
      }),
    }));

    await db.withTransaction(async tx => {
      for (const { plan, rows } of prepared) {
        const survivor = plan.keepIds[0] ?? plan.newRows[0]?.id;
        if (plan.dropIds.length > 0 && survivor === undefined) {
          result.entitiesLeftIntact += 1;
          continue;
        }

        await insertRows(tx, rows);
        result.embeddingsInserted += rows.length;

        if (plan.dropIds.length > 0 && survivor !== undefined) {
          const carried = await tx.runSql<{ id: string }>(CARRY_OVER_SQL, {
            survivor,
            dropIds: plan.dropIds,
          });
          result.matchesCarriedOver += carried.length;

          await revalidateDismissedMatchesByMatchIds(tx, {
            matchResultIds: carried.map(row => row.id),
            reason: REASON_BY_TYPE[entityType],
            changeId: plan.changeId,
          });

          const deletedMatches = await tx.runSql<{ id: string }>(
            `DELETE FROM entity_screening_match_results
              WHERE entity_embedding_id = ANY(:dropIds)
              RETURNING id`,
            { dropIds: plan.dropIds },
          );
          result.unreviewedMatchesDeleted += deletedMatches.length;

          const deletedEmbeddings = await tx.runSql<{ id: string }>(
            `DELETE FROM entity_screening_embeddings
              WHERE id = ANY(:dropIds)
              RETURNING id`,
            { dropIds: plan.dropIds },
          );
          result.embeddingsDeleted += deletedEmbeddings.length;
        }

        // The survivors' scores were earned against a text set that has now
        // changed. Touching `updated_at` puts them in the next matching
        // cycle (`fetchRecentEmbeddingIdsDb` keys on it) so the carried-over
        // hits get honest scores.
        if (plan.keepIds.length > 0) {
          await tx.runSql(
            `UPDATE entity_screening_embeddings
                SET updated_at = CURRENT_TIMESTAMP
              WHERE id = ANY(:keepIds)`,
            { keepIds: plan.keepIds },
          );
        }
      }
    });
  }

  return result;
};

const insertRows = async (
  tx: DbTransactionCtx,
  prepared: {
    row: InsertEmbeddingDbParams;
    index: number;
    placeholder: string;
    binding: unknown;
  }[],
): Promise<void> => {
  for (let i = 0; i < prepared.length; i += INSERT_BATCH_SIZE) {
    const batch = prepared.slice(i, i + INSERT_BATCH_SIZE);

    const values = batch
      .map(
        ({ index, placeholder }) =>
          `(:id_${index}, :entityId_${index}, :entityType_${index}, :text_${index},
            :model_${index}, :composedOf_${index}, ${placeholder},
            :createdAt_${index}, :updatedAt_${index})`,
      )
      .join(', ');

    const binds: Record<string, unknown> = {};
    for (const { row, index, binding } of batch) {
      binds[`id_${index}`] = row.id;
      binds[`entityId_${index}`] = row.entityId;
      binds[`entityType_${index}`] = row.entityType;
      binds[`text_${index}`] = row.textForEmbedding;
      binds[`model_${index}`] = row.modelUsed;
      binds[`composedOf_${index}`] = JSON.stringify(row.composedOf);
      binds[`embedding_${index}`] = binding;
      binds[`createdAt_${index}`] = row.createdAt;
      binds[`updatedAt_${index}`] = row.updatedAt;
    }

    await tx.runSql(
      `INSERT INTO entity_screening_embeddings (
         id, entity_id, entity_type, text_for_embedding,
         model_used, composed_of, embedding, created_at, updated_at
       ) VALUES ${values}`,
      binds,
    );
  }
};
